import { LoginPinFrequencyModel } from "../Models/Employee/LoginFrequencyModel";
import { PinFrequency, PinFrequencyDocumentType } from "../Models/UI/Enums";

type PinFrequencyDocumentTypeLabelType =
    "ConsultationRequestLabel"
    | "DocumentLabel"
    | "ExamRequestLabel"
    | "FormLabel"
    | "PrescriptionLabel"
    | "ObstetricalFile_QuebecObstetricalFile2Label"
    | "ObstetricalFile_OntarioObstetricalFile2Label"
    | "ObstetricalFile_BritishColumbiaObstetricalFile2Label"
    | "";

type PinFrequencyLabelKey =
    "DocumentSigningPinOptions_AlwaysLabel"
    | "DocumentSigningPinOptions_EverySessionLabel"
    | "DocumentSigningPinOptions_EveryDayLabel"
    | "DocumentSigningPinOptions_WeeklyLabel"
    | "DocumentSigningPinOptions_MonthlyLabel"
    | "DocumentSigningPinOptions_NeverLabel"
    | "";

class PinFrequencyHelper {
    /**
     * Get the document types which can have a PIN frequency.
     * @param enableQuebecObstetricalPin Whether Quebec obstetrical files are enabled.
     * @param enableOntarioObstetricalPin Whether Ontario obstetrical file are enabled.
     * @param enableBritishColumbiaObstetricalPin Whether British Columbia obstetrical files are enabled.
     */
    public static getOptions(enableQuebecObstetricalPin: boolean, enableOntarioObstetricalPin: boolean, enableBritishColumbiaObstetricalPin: boolean): Readonly<PinFrequencyDocumentType[]> {
        let options = [
            PinFrequencyDocumentType.ConsultationRequest,
            PinFrequencyDocumentType.Document,
            PinFrequencyDocumentType.ExamRequest,
            PinFrequencyDocumentType.Form,
            PinFrequencyDocumentType.Prescription
        ];

        if (enableQuebecObstetricalPin || enableOntarioObstetricalPin || enableBritishColumbiaObstetricalPin) {
            options = [...options, PinFrequencyDocumentType.ObstetricalFile];
        }

        return options;
    }

    /**
     * Get the label translation key for a PIN frequency document type.
     * @param documentType The document type.
     * @param enableQuebecObstetricalPin Whether Quebec obstetrical files are enabled.
     * @param enableOntarioObstetricalPin Whether Ontario obstetrical files are enabled.
     * @param enableBritishColumbiaObstetricalPin Whether British Columbia obstetrical files are enabled.
     */
    public static getLabelByPinFrequencyDocumentType(documentType: PinFrequencyDocumentType, enableQuebecObstetricalPin: boolean, enableOntarioObstetricalPin: boolean, enableBritishColumbiaObstetricalPin: boolean): PinFrequencyDocumentTypeLabelType {
        switch (documentType) {
            case PinFrequencyDocumentType.ConsultationRequest:
                return "ConsultationRequestLabel";

            case PinFrequencyDocumentType.Document:
                return "DocumentLabel";

            case PinFrequencyDocumentType.ExamRequest:
                return "ExamRequestLabel";

            case PinFrequencyDocumentType.Form:
                return "FormLabel";

            case PinFrequencyDocumentType.Prescription:
                return "PrescriptionLabel";

            case PinFrequencyDocumentType.ObstetricalFile:
            {
                if (enableQuebecObstetricalPin)
                    return "ObstetricalFile_QuebecObstetricalFile2Label";
                else if (enableOntarioObstetricalPin)
                    return "ObstetricalFile_OntarioObstetricalFile2Label";
                else if (enableBritishColumbiaObstetricalPin)
                    return "ObstetricalFile_BritishColumbiaObstetricalFile2Label";
                else
                    return "";
            }

            default:
                return "";
        }
    }

    /**
     * Get the label translation key for a PIN frequency.
     * @param pinFrequency The PIN frequency.
     */
    public static getLabelByPinFrequency(pinFrequency: PinFrequency): PinFrequencyLabelKey {
        switch (pinFrequency) {
            case PinFrequency.Always:
                return "DocumentSigningPinOptions_AlwaysLabel";
            case PinFrequency.EverySession:
                return "DocumentSigningPinOptions_EverySessionLabel";
            case PinFrequency.EveryDay:
                return "DocumentSigningPinOptions_EveryDayLabel";
            case PinFrequency.Weekly:
                return "DocumentSigningPinOptions_WeeklyLabel";
            case PinFrequency.Monthly:
                return "DocumentSigningPinOptions_MonthlyLabel";
            case PinFrequency.Never:
                return "DocumentSigningPinOptions_NeverLabel";
            default:
                return "";
        }
    }

    /**
     * Get the PIN frequency for a document type.
     * @param model The login PIN frequency model to read.
     * @param documentType The document type whose field to get.
     */
    public static getFrequency(model: LoginPinFrequencyModel, documentType: PinFrequencyDocumentType): PinFrequency {
        if (!model)
            return PinFrequency.Always;

        switch (documentType) {
            case PinFrequencyDocumentType.ConsultationRequest:
                return model.consultationRequestFrequency;
            case PinFrequencyDocumentType.Document:
                return model.documentFrequency;
            case PinFrequencyDocumentType.ExamRequest:
                return model.examRequestFrequency;
            case PinFrequencyDocumentType.Form:
                return model.formFrequency;
            case PinFrequencyDocumentType.Prescription:
                return model.prescriptionFrequency;
            case PinFrequencyDocumentType.ObstetricalFile:
                return model.obstetricalFileFrequency;
            default:
                return PinFrequency.Always;
        }
    }

    /**
     * Set the PIN frequency for a document type.
     * @param model The login PIN frequency model to update.
     * @param documentType The document type whose field to change.
     * @param value The new PIN frequency value.
     * @returns The updated login PIN frequency model.
     */
    public static setFrequency(model: LoginPinFrequencyModel, documentType: PinFrequencyDocumentType, value: PinFrequency): LoginPinFrequencyModel {
        if (model) {
            switch (documentType) {
                case PinFrequencyDocumentType.ConsultationRequest:
                    model.consultationRequestFrequency = value;
                    break;
                case PinFrequencyDocumentType.Document:
                    model.documentFrequency = value;
                    break;
                case PinFrequencyDocumentType.ExamRequest:
                    model.examRequestFrequency = value;
                    break;
                case PinFrequencyDocumentType.Form:
                    model.formFrequency = value;
                    break;
                case PinFrequencyDocumentType.Prescription:
                    model.prescriptionFrequency = value;
                    break;
                case PinFrequencyDocumentType.ObstetricalFile:
                    model.obstetricalFileFrequency = value;
                    break;
            }
        }

        return model;
    }

    /**
     * Set the PIN frequency token for a document type.
     * @param model The login PIN frequency model to update.
     * @param documentType The document type whose field to change.
     * @param value The new PIN frequency token.
     * @returns The updated login PIN frequency model.
     */
    public static setFrequencyToken(model: LoginPinFrequencyModel, documentType: PinFrequencyDocumentType, value: string): LoginPinFrequencyModel {
        if (model) {
            switch (documentType) {
                case PinFrequencyDocumentType.ConsultationRequest:
                    model.consultationRequestToken = value;
                    break;
                case PinFrequencyDocumentType.Document:
                    model.documentToken = value;
                    break;
                case PinFrequencyDocumentType.ExamRequest:
                    model.examRequestToken = value;
                    break;
                case PinFrequencyDocumentType.Form:
                    model.formToken = value;
                    break;
                case PinFrequencyDocumentType.Prescription:
                    model.prescriptionToken = value;
                    break;
                case PinFrequencyDocumentType.ObstetricalFile:
                    model.obstetricalFileToken = value;
                    break;
            }
        }

        return model;
    }
}

export { PinFrequencyHelper };
